import { useMemo } from "react"

import { useSelector } from "react-redux"

import ParticipantDetails from "@components/TeeupPeople/Modals/ParticipantDetails"
import AttendanceModal from "@components/TeeupPeople/Modals/Attendance"
import { selectMessages, selectUserId } from "@selectors/activeTeeup"
import { teeupUserStatusKeys } from "@configs/enums"
import { useTeeupPeopleStore } from "stores/teeupPeopleStore"

import Block from "../Block"
import { orderPeopleForDisplay, safeRelativeTime, safeTimeValue } from "../../peopleOrder"

import ParticipantItem from "./ParticipantItem"

import "./index.scss"

const MAX_FLOW_ROWS = 3

// Reused from the removed standalone Activity Stream section (2026-07-05 restructure) --
// gameplan suggestion entries ride the same `messages` array as chat but carry no `.text`
// (their content lives in `value`/`customDate`/`customTime`/`details`) and no creation
// timestamp of their own worth trusting as "posted at" (see peopleOrder.safeTimeValue guard).
const suggestionText = message => {
  if (!message?.isSuggestion) return null
  const detail =
    message.value || message.customDate || message.customTime || message.details
  if (message.type === "where") return `suggested a place${detail ? `: ${detail}` : ""}`
  if (message.type === "when") return `suggested a time${detail ? `: ${detail}` : ""}`
  return "made a suggestion"
}

const messageTime = message => message?.timestamp ?? message?.createdAt

const messageText = message => {
  const text = (message?.text || message?.message || "").toString().trim()
  if (text) return `messaged: "${text.length > 60 ? `${text.slice(0, 57)}...` : text}"`
  return suggestionText(message) || "messaged"
}

const messageActorId = message =>
  message?.senderId ?? message?.userId ?? message?.createdBy

// People's Activity concept (QJ 2026-07-05): each row's per-person FLOW is the visible reason
// for its rank in the list -- the same three signals that feed the strip's own sort
// (peopleOrder.js): join, latest recorded status/teeup activity, and their own chat/suggestion
// messages, merged and time-sorted. Honest-signal law: any event whose timestamp fails the
// epoch guard (peopleOrder.safeTimeValue) is dropped rather than shown with a garbage age --
// this is the fix for the "Quentin 56y" bug (root cause: some records carry a real, parseable,
// but SENTINEL epoch-zero timestamp instead of a null one; see peopleOrder.js).
const buildActivityFlow = (person, messages) => {
  const items = []

  if (person?.joinedAt) {
    const t = safeTimeValue(person.joinedAt)
    if (t !== null) items.push({ key: "joined", text: "joined", t })
  }

  if (person?.lastTeeupActivity && person?.lastTeeupActivityCreatedAt) {
    const t = safeTimeValue(person.lastTeeupActivityCreatedAt)
    if (t !== null) items.push({ key: "last-activity", text: person.lastTeeupActivity, t })
  }

  // FLOWS-FIX 2026-07-05 (dev-verify session): the dev backend's injected suggestion-message
  // (devBackendShim.fetchSuggestionMessagesForCo) carries NO timestamp/createdAt field at all --
  // only createdBy/creator -- so safeTimeValue(undefined) is null for every "suggested a
  // time"/"suggested a place" event. The previous `if (t === null) return` silently dropped every
  // one of those items from the flow, even though the identical events render fine in
  // Conversation > All activity (root cause of QJ-reported empty per-person flows on fixtures
  // whose only activity was suggestions, e.g. "Test"/QA-DECIDE500-CLEAN). A MISSING creation time
  // is not the same as the epoch-guard's garbage SENTINEL time (peopleOrder.safeTimeValue already
  // handles that case) -- still show the attributed action, just without a time badge, sorted
  // after every item that does have a real timestamp.
  messages
    ?.filter(m => messageActorId(m) === person?.id)
    .forEach((m, i) => {
      const t = safeTimeValue(messageTime(m))
      items.push({ key: `msg-${m.id ?? i}`, text: messageText(m), t })
    })

  return items
    .sort((a, b) => (b.t ?? -Infinity) - (a.t ?? -Infinity))
    .slice(0, MAX_FLOW_ROWS)
    .map(({ key, text, t }) => ({ id: key, text, time: t !== null ? safeRelativeTime(t) : "" }))
}

const isGoing = status =>
  status === teeupUserStatusKeys.going ||
  status === teeupUserStatusKeys.onmyway ||
  status === teeupUserStatusKeys.arrived

const isNotGoing = status =>
  status === teeupUserStatusKeys.notgoing ||
  status === teeupUserStatusKeys.declined ||
  status === teeupUserStatusKeys.droppedout

// People Card Detail-Groups spec (Notion d45923fee2264c559a5512601920958c), restructured
// 2026-07-05 per QJ:
//   - "Info" is now a STATISTICAL SUMMARY (counts only, honestly computable from client data --
//     no fabricated fields), not the roster.
//   - The roster itself is now the PEOPLE'S ACTIVITY view: one row per person, in the SAME order
//     as the collapsed strip (peopleOrder.orderPeopleForDisplay -- organizers, then going, then
//     everyone else, most-recent-activity-first within each group), each row carrying a compact
//     FLOW of what that person did (see buildActivityFlow above). The standalone "Activity
//     Stream" section is gone -- its raw messages are the material for these per-person flows
//     instead of a second, teeup-wide chronological list (Conversation > All activity already
//     owns that job).
//   - The viewer's OWN row is self-pinned at the top (QJ 2026-07-05 refinement) ahead of the
//     organizer/going/rest ordering below it, and carries the attendance control INLINE
//     (AttendanceModal anchored) instead of a separate standalone "Your Status" block -- status
//     changes now happen right there on the self row.
const Participants = ({ people }) => {
  const messages = useSelector(selectMessages)
  const userId = useSelector(selectUserId)
  const setCurrentParticipant = useTeeupPeopleStore(
    state => state.setCurrentParticipant
  )
  const setIsParticipantDetailsModalOpen = useTeeupPeopleStore(
    state => state.setIsParticipantDetailsModalOpen
  )

  const organizerId = useMemo(
    () => people.find(p => p.isOrganiser)?.id,
    [people]
  )

  const currentUser = useMemo(
    () => people.find(p => p.id === userId),
    [people, userId]
  )

  // Self-pin wins in every case -- including when the viewer IS the organizer, they are simply
  // first with their own crown -- then everyone else in the standing strip order.
  const rows = useMemo(() => {
    const rest = orderPeopleForDisplay(people).filter(p => p.id !== userId)
    return currentUser ? [currentUser, ...rest] : orderPeopleForDisplay(people)
  }, [people, userId, currentUser])

  const flows = useMemo(() => {
    const map = {}
    rows.forEach(p => {
      map[p.id] = buildActivityFlow(p, messages)
    })
    return map
  }, [rows, messages])

  const stats = useMemo(() => {
    const total = people.length
    const organizers = people.filter(p => p.isOrganiser).length
    const going = people.filter(p => isGoing(p.status)).length
    const notGoing = people.filter(p => isNotGoing(p.status)).length
    const pending = Math.max(0, total - going - notGoing)
    const timezones = new Set(people.map(p => p.timezone).filter(Boolean)).size
    const latestMs = people.reduce((max, p) => {
      const t = safeTimeValue(p.lastTeeupActivityCreatedAt)
      return t !== null && t > max ? t : max
    }, 0)
    const latest = latestMs ? safeRelativeTime(latestMs) : ""
    return { total, organizers, going, notGoing, pending, timezones, latest }
  }, [people])

  const handleParticipantDetails = participant => {
    setCurrentParticipant(participant)
    setIsParticipantDetailsModalOpen(true)
  }

  return (
    <>
      <Block className="people-info-group" data-testid="teeup-detail-people-info-group">
        <h4>Info</h4>
        <p className="people-info-stats" data-testid="teeup-detail-people-stats">
          {stats.total} people &middot; {stats.organizers} organizer
          {stats.organizers === 1 ? "" : "s"} &middot; {stats.going} going &middot;{" "}
          {stats.pending} pending &middot; {stats.notGoing} not going
          {stats.timezones > 1 ? ` · ${stats.timezones} time zones` : ""}
          {stats.latest ? ` · latest activity ${stats.latest} ago` : ""}
        </p>
      </Block>

      <Block
        className="people-activity-group"
        data-testid="teeup-detail-people-activity-group">
        <h4>People</h4>

        <ul className="people-participant-block">
          {rows.map(participant => (
            <ParticipantItem
              key={participant.id}
              participant={participant}
              activityFlow={flows[participant.id]}
              onClick={handleParticipantDetails}
              rightSlot={
                participant.id === userId ? (
                  <AttendanceModal user={currentUser} anchored />
                ) : null
              }
            />
          ))}
        </ul>
      </Block>

      <ParticipantDetails organizerId={organizerId} />
    </>
  )
}

export default Participants
